import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import {
  TrendUp, CurrencyInr, Calendar, UsersThree, Pill, Warning, ArrowUpRight, ChartLineUp, Pulse,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";

const CURRENCY = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

function KpiCard({ icon: Icon, label, value, sub, tone = "forest", testId }) {
  const toneMap = {
    forest: "bg-forest text-cream",
    terracotta: "bg-terracotta text-cream",
    sand: "bg-sand text-forest",
    sage: "bg-sage text-cream",
    ivory: "bg-ivory text-forest border border-border/60",
  };
  return (
    <div className={`rounded-2xl p-6 ${toneMap[tone]}`} data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.2em] opacity-70">{label}</div>
          <div className="font-serif-display text-3xl mt-2 leading-none">{value}</div>
          {sub && <div className="text-xs mt-2 opacity-75">{sub}</div>}
        </div>
        <div className="shrink-0 w-11 h-11 rounded-xl bg-black/10 flex items-center justify-center">
          <Icon size={22} weight="duotone" />
        </div>
      </div>
    </div>
  );
}

export default function Overview() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = () => {
    api.get("/admin/overview")
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Failed to load overview"));
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // refresh every minute
    return () => clearInterval(t);
  }, []);

  if (err) return <div className="p-8 text-destructive" data-testid="overview-error">{err}</div>;
  if (!data) return <div className="p-8 text-muted-foreground" data-testid="overview-loading">Loading overview…</div>;

  const { revenue, admissions, leads, medicines, trend, low_stock } = data;

  const sourceData = [
    { name: "Walk-in", value: admissions.walk_ins, fill: "#c06b40" },
    { name: "Online", value: admissions.online, fill: "#3d5a3d" },
    { name: "Phone", value: admissions.phone, fill: "#7a8a64" },
    { name: "WhatsApp", value: admissions.whatsapp, fill: "#25D366" },
  ].filter(d => d.value > 0);

  return (
    <div data-testid="admin-overview" className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-serif-display text-4xl">Overview</h1>
          <p className="text-muted-foreground mt-2">
            Live snapshot of your clinic — revenue, patient visits, leads and stock health.
          </p>
        </div>
        <div className="text-xs text-muted-foreground" data-testid="overview-generated-at">
          Updated {new Date(data.generated_at).toLocaleString()}
        </div>
      </div>

      {/* Revenue KPIs */}
      <section>
        <h2 className="text-xs uppercase tracking-[0.25em] text-terracotta mb-3">Financial snapshot</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard testId="kpi-revenue-total" icon={CurrencyInr} tone="forest"
            label="Total revenue" value={CURRENCY(revenue.total)}
            sub={`${revenue.bills_count} bill${revenue.bills_count === 1 ? "" : "s"} raised`}/>
          <KpiCard testId="kpi-revenue-month" icon={TrendUp} tone="terracotta"
            label="This month" value={CURRENCY(revenue.this_month)}
            sub="Bills dated this calendar month"/>
          <KpiCard testId="kpi-outstanding" icon={Warning} tone="sand"
            label="Outstanding" value={CURRENCY(revenue.outstanding)}
            sub={`Paid ${CURRENCY(revenue.amount_paid)} of ${CURRENCY(revenue.total)}`}/>
          <KpiCard testId="kpi-medicine-revenue" icon={Pill} tone="sage"
            label="Medicine sales" value={CURRENCY(revenue.medicine_revenue)}
            sub={`Admission fees ${CURRENCY(revenue.admission_revenue)}`}/>
        </div>
      </section>

      {/* Admissions + Leads */}
      <section>
        <h2 className="text-xs uppercase tracking-[0.25em] text-terracotta mb-3">Patients & leads</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard testId="kpi-admissions-total" icon={Calendar} tone="ivory"
            label="Total visits" value={admissions.total}
            sub={`${admissions.this_month} this month · ${admissions.pending} pending`}/>
          <KpiCard testId="kpi-admissions-today" icon={Pulse} tone="ivory"
            label="Today's visits" value={admissions.today}
            sub={`Walk-ins ${admissions.walk_ins} · Online ${admissions.online}`}/>
          <KpiCard testId="kpi-leads" icon={UsersThree} tone="ivory"
            label="Leads / contacts" value={leads.total}
            sub={`${leads.this_week} new this week`}/>
          <KpiCard testId="kpi-conversion" icon={ChartLineUp} tone="ivory"
            label="Conversion rate" value={`${leads.conversion_rate}%`}
            sub="Enquiries → booked visits"/>
        </div>
      </section>

      {/* Charts row */}
      <section className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 bg-ivory border border-border/60 rounded-2xl p-6" data-testid="chart-trend">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif-display text-2xl">Revenue & visits — last 6 months</h3>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5ded0" />
                <XAxis dataKey="label" stroke="#5b6252" fontSize={12} />
                <YAxis yAxisId="left" stroke="#3d5a3d" fontSize={12} />
                <YAxis yAxisId="right" orientation="right" stroke="#c06b40" fontSize={12} />
                <Tooltip
                  contentStyle={{ background: "#fffdf7", border: "1px solid #e5ded0", borderRadius: 10 }}
                  formatter={(val, name) => (name === "revenue" ? CURRENCY(val) : val)}
                />
                <Legend iconType="plainline" />
                <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#3d5a3d" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line yAxisId="right" type="monotone" dataKey="bookings" stroke="#c06b40" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-ivory border border-border/60 rounded-2xl p-6" data-testid="chart-source">
          <h3 className="font-serif-display text-2xl mb-4">Booking source</h3>
          {sourceData.length === 0 ? (
            <div className="text-sm text-muted-foreground py-10 text-center">No bookings yet.</div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={sourceData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={3}>
                    {sourceData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#fffdf7", border: "1px solid #e5ded0", borderRadius: 10 }} />
                  <Legend iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </section>

      {/* Top medicines + Low stock */}
      <section className="grid gap-4 lg:grid-cols-2">
        <div className="bg-ivory border border-border/60 rounded-2xl p-6" data-testid="top-medicines">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif-display text-2xl">Top medicines</h3>
            <span className="text-xs text-muted-foreground">by number of bills</span>
          </div>
          {medicines.top.length === 0 ? (
            <div className="text-sm text-muted-foreground py-10 text-center">
              Medicines will appear here as you record them on bills.
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={medicines.top} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5ded0" />
                  <XAxis dataKey="name" stroke="#5b6252" fontSize={11} interval={0} angle={-15} textAnchor="end" height={60} />
                  <YAxis stroke="#5b6252" fontSize={12} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: "#fffdf7", border: "1px solid #e5ded0", borderRadius: 10 }}
                    formatter={(val, name) => (name === "revenue" ? CURRENCY(val) : val)}
                  />
                  <Bar dataKey="count" fill="#3d5a3d" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="bg-ivory border border-border/60 rounded-2xl p-6" data-testid="low-stock-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif-display text-2xl">Low stock alert</h3>
            <Link to="/admin/stock" className="text-xs text-terracotta hover:underline flex items-center gap-1" data-testid="low-stock-manage">
              Manage stock <ArrowUpRight size={14}/>
            </Link>
          </div>
          {low_stock.count === 0 ? (
            <div className="text-sm text-muted-foreground py-10 text-center">
              All tracked items are above their reorder level.
            </div>
          ) : (
            <ul className="divide-y divide-border/50">
              {low_stock.items.map(item => (
                <li key={item.id} className="py-3 flex items-center justify-between" data-testid={`low-stock-${item.id}`}>
                  <div>
                    <div className="font-medium text-sm">{item.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {item.current_qty} {item.unit} left · reorder at {item.reorder_level}
                    </div>
                  </div>
                  <span className="inline-block px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.15em] bg-terracotta text-cream">
                    Low
                  </span>
                </li>
              ))}
              {low_stock.count > low_stock.items.length && (
                <li className="py-3 text-xs text-muted-foreground">
                  +{low_stock.count - low_stock.items.length} more…
                </li>
              )}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
